import { formatDuration, selectRecipeMetaVisibility, useAppSelector, useT, DIFFICULTY_LABEL_KEYS, type Recipe } from '@reciperoll/core'
import { Link } from 'react-router-dom'
import { Activity, BarChart2, Check, Clock, Star } from 'react-feather'
import FavouriteButton from './Favourites/FavouriteButton'
import { RecipeImage } from './RecipeImage'

interface RecipeGridItemProps {
  recipe: Recipe
  // Whether this recipe has an in-progress cook (shown as a badge on the image).
  isCooking?: boolean
}

export function RecipeGridItem({ recipe, isCooking = false }: RecipeGridItemProps) {
  const t = useT()
  const showMeta = useAppSelector(selectRecipeMetaVisibility)
  const duration = showMeta.duration && recipe.cookingDuration ? formatDuration(recipe.cookingDuration, t) : null

  return (
    <Link
      to={`/recipe/${recipe.id}`}
      className="group block rounded-2xl overflow-hidden bg-surface border border-line shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 [content-visibility:auto] [contain-intrinsic-size:auto_260px]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-terracotta-wash">
        <RecipeImage
          recipe={recipe}
          className="transition-transform duration-500 group-hover:scale-105"
        />
        {/* Scrim so white badges stay legible over any photo. */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />

        <FavouriteButton
          recipe={recipe}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center"
        />

        {(isCooking || recipe.cooked) && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
            {isCooking && (
              <span className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 text-[11px] font-bold text-terracotta-ink">
                <Activity className="w-3 h-3" strokeWidth={2.5} />
                {t("recipe.cooking")}
              </span>
            )}
            {recipe.cooked && (
              <span className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 text-[11px] font-bold text-green-700">
                <Check className="w-3 h-3" strokeWidth={3} />
                {t("recipe.cooked")}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Fixed-height body (2-line title slot + always-present meta row) so every
          card is a uniform height in the rails and grid, regardless of title
          length or missing meta. */}
      <div className="p-3.5">
        <h3 className="text-[15px] font-semibold text-ink leading-snug line-clamp-2 min-h-[2.6rem] group-hover:text-terracotta-ink transition-colors">
          {recipe.title}
        </h3>
        <div className="flex items-center gap-3 mt-1.5 h-4 text-xs font-medium text-ink-faint">
          {duration && (
            <span className="flex items-center gap-1 tabular-nums">
              <Clock className="w-3.5 h-3.5" strokeWidth={2} />
              {duration}
            </span>
          )}
          {showMeta.difficulty && recipe.difficulty && (
            <span className="flex items-center gap-1">
              <BarChart2 className="w-3.5 h-3.5" strokeWidth={2} />
              {t(DIFFICULTY_LABEL_KEYS[recipe.difficulty])}
            </span>
          )}
          {showMeta.rating && recipe.rating != null && (
            <span className="flex items-center gap-1 tabular-nums">
              <Star className="w-3.5 h-3.5 text-terracotta fill-terracotta" />
              {recipe.rating}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}
